/**
 * Backend sync handler.
 *
 * Provides functions that mirror the localStorage handler's persistence
 * API but synchronise data with the shared backend when the user is
 * authenticated.  Falls back to localStorage for unauthenticated or
 * offline access.
 *
 * Sync strategy:
 * - On login  → pull server state into localStorage (server wins).
 * - On save   → write to localStorage immediately, then push to server.
 * - On logout → server already has state; localStorage is kept as cache.
 *
 * This module is imported alongside (not instead of) local-storage-handler.
 * Components that need sync call these wrappers; components that only need
 * local cache continue to use local-storage-handler directly.
 */
import syncApi from "@interceptors/sync-instance";
import { getAccessToken } from "./local-storage-handler";
import {
  SavedChartLayout,
  SavedChartSettings,
  ChartTemplate,
  CustomTimeframe,
  saveChartLayout as localSaveLayout,
  loadChartLayout as localLoadLayout,
  saveDrawings as localSaveDrawings,
  loadDrawings as localLoadDrawings,
  saveChartSettings as localSaveSettings,
  loadChartSettings as localLoadSettings,
  loadTemplates as localLoadTemplates,
  saveTemplate as localSaveTemplate,
  deleteTemplate as localDeleteTemplate,
  setDefaultTemplate as localSetDefaultTemplate,
  saveCustomTimeframes as localSaveCustomTimeframes,
  loadCustomTimeframes as localLoadCustomTimeframes,
} from "./local-storage-handler";

// ── Helpers ───────────────────────────────────────────────────────

const API_PREFIX = "/api/v1";

/** True when we have a token and should attempt server sync. */
function canSync(): boolean {
  return !!getAccessToken();
}

// ── Chart Layout Sync ─────────────────────────────────────────────

/** Save layout locally + push to server. */
export async function syncSaveChartLayout(layout: SavedChartLayout): Promise<void> {
  // Always write to localStorage first (instant)
  localSaveLayout(layout);

  if (!canSync()) return;

  try {
    await syncApi.post(`${API_PREFIX}/charts/layouts`, {
      name: "default",
      layout_data: layout,
      is_active: true,
    });
  } catch (e) {
    console.warn("[Sync] Failed to push chart layout to server:", e);
  }
}

/** Load layout: try server first, fall back to localStorage. */
export async function syncLoadChartLayout(): Promise<SavedChartLayout | null> {
  if (!canSync()) return localLoadLayout();

  try {
    const response = await syncApi.get(`${API_PREFIX}/charts/layouts`);
    const layouts = response.data;
    if (Array.isArray(layouts) && layouts.length > 0) {
      const active = layouts.find((l: any) => l.is_active) || layouts[0];
      const serverLayout = active.layout_data as SavedChartLayout;
      // Update local cache with server state
      localSaveLayout(serverLayout);
      return serverLayout;
    }
  } catch (e) {
    console.warn("[Sync] Failed to load layouts from server, using localStorage:", e);
  }

  return localLoadLayout();
}

// ── Drawing Sync ──────────────────────────────────────────────────

/** Save drawings locally + push to server. */
export async function syncSaveDrawings(
  symbolKey: string,
  timeframe: string,
  drawings: any[]
): Promise<void> {
  localSaveDrawings(symbolKey, timeframe, drawings);

  if (!canSync()) return;

  try {
    await syncApi.put(
      `${API_PREFIX}/charts/drawings/${encodeURIComponent(symbolKey)}/${encodeURIComponent(timeframe)}`,
      { drawings }
    );
  } catch (e) {
    console.warn("[Sync] Failed to push drawings to server:", e);
  }
}

/** Load drawings: try server first, fall back to localStorage. */
export async function syncLoadDrawings(
  symbolKey: string,
  timeframe: string
): Promise<any[]> {
  if (!canSync()) return localLoadDrawings(symbolKey, timeframe);

  try {
    const response = await syncApi.get(
      `${API_PREFIX}/charts/drawings/${encodeURIComponent(symbolKey)}/${encodeURIComponent(timeframe)}`
    );
    const serverDrawings = response.data?.drawings;
    if (Array.isArray(serverDrawings)) {
      localSaveDrawings(symbolKey, timeframe, serverDrawings);
      return serverDrawings;
    }
  } catch (e) {
    console.warn("[Sync] Failed to load drawings from server, using localStorage:", e);
  }

  return localLoadDrawings(symbolKey, timeframe);
}

// ── Chart Settings Sync ───────────────────────────────────────────

/** Save settings locally + push to server. */
export async function syncSaveChartSettings(settings: SavedChartSettings): Promise<void> {
  localSaveSettings(settings);

  if (!canSync()) return;

  try {
    await syncApi.put(`${API_PREFIX}/charts/settings`, settings);
  } catch (e) {
    console.warn("[Sync] Failed to push chart settings to server:", e);
  }
}

/** Load settings: try server first, fall back to localStorage. */
export async function syncLoadChartSettings(): Promise<SavedChartSettings> {
  if (!canSync()) return localLoadSettings();

  try {
    const response = await syncApi.get(`${API_PREFIX}/charts/settings`);
    if (response.data) {
      const serverSettings = response.data as SavedChartSettings;
      localSaveSettings(serverSettings);
      return serverSettings;
    }
  } catch (e) {
    console.warn("[Sync] Failed to load settings from server, using localStorage:", e);
  }

  return localLoadSettings();
}

// ── Watchlist Sync ────────────────────────────────────────────────

/** Fetch all watchlists from the server. */
export async function syncGetWatchlists(): Promise<any[]> {
  if (!canSync()) return [];

  try {
    const response = await syncApi.get(`${API_PREFIX}/watchlists`);
    return response.data || [];
  } catch (e) {
    console.warn("[Sync] Failed to load watchlists:", e);
    return [];
  }
}

/** Create a watchlist on the server. */
export async function syncCreateWatchlist(name: string, symbols: string[] = []): Promise<any> {
  const response = await syncApi.post(
    `${API_PREFIX}/watchlists`,
    { name, symbols }
  );
  return response.data;
}

/** Add a symbol to a watchlist on the server. */
export async function syncAddSymbol(watchlistId: string, symbol: string): Promise<void> {
  await syncApi.post(
    `${API_PREFIX}/watchlists/${watchlistId}/symbols`,
    { symbol }
  );
}

/** Remove a symbol from a watchlist on the server. */
export async function syncRemoveSymbol(watchlistId: string, symbol: string): Promise<void> {
  await syncApi.delete(
    `${API_PREFIX}/watchlists/${watchlistId}/symbols/${encodeURIComponent(symbol)}`
  );
}

// ── User Settings Sync ────────────────────────────────────────────

/** Fetch user settings from the server. */
export async function syncGetUserSettings(): Promise<Record<string, any> | null> {
  if (!canSync()) return null;

  try {
    const response = await syncApi.get(`${API_PREFIX}/settings`);
    return response.data;
  } catch (e) {
    console.warn("[Sync] Failed to load user settings:", e);
    return null;
  }
}

/** Push user settings to the server. */
export async function syncSaveUserSettings(settings: Record<string, any>): Promise<void> {
  if (!canSync()) return;

  try {
    await syncApi.put(`${API_PREFIX}/settings`, settings);
  } catch (e) {
    console.warn("[Sync] Failed to push user settings:", e);
  }
}

// ── Template Sync ─────────────────────────────────────────────────

/** Save a template locally + push all templates to server as user setting. */
export async function syncSaveTemplate(template: ChartTemplate): Promise<void> {
  localSaveTemplate(template);

  if (!canSync()) return;

  try {
    const allTemplates = localLoadTemplates();
    await syncApi.put(`${API_PREFIX}/settings/templates`, { templates: allTemplates });
  } catch (e) {
    console.warn("[Sync] Failed to push templates to server:", e);
  }
}

/** Delete a template locally + push updated list to server. */
export async function syncDeleteTemplate(id: string): Promise<void> {
  localDeleteTemplate(id);

  if (!canSync()) return;

  try {
    const allTemplates = localLoadTemplates();
    await syncApi.put(`${API_PREFIX}/settings/templates`, { templates: allTemplates });
  } catch (e) {
    console.warn("[Sync] Failed to push template deletion to server:", e);
  }
}

/** Set/unset default template locally + push to server. */
export async function syncSetDefaultTemplate(id: string | null): Promise<void> {
  localSetDefaultTemplate(id);

  if (!canSync()) return;

  try {
    const allTemplates = localLoadTemplates();
    await syncApi.put(`${API_PREFIX}/settings/templates`, { templates: allTemplates });
  } catch (e) {
    console.warn("[Sync] Failed to push template default to server:", e);
  }
}

/** Load templates: try server first, fall back to localStorage. */
export async function syncLoadTemplates(): Promise<ChartTemplate[]> {
  if (!canSync()) return localLoadTemplates();

  try {
    const response = await syncApi.get(`${API_PREFIX}/settings/templates`);
    const serverTemplates = response.data?.templates;
    if (Array.isArray(serverTemplates) && serverTemplates.length > 0) {
      // Hydrate local cache with server state
      serverTemplates.forEach((t: ChartTemplate) => localSaveTemplate(t));
      return serverTemplates;
    }
  } catch (e) {
    console.warn("[Sync] Failed to load templates from server, using localStorage:", e);
  }

  return localLoadTemplates();
}

// ── Custom Timeframe Sync ─────────────────────────────────────────

/** Save custom timeframes locally + push to server. */
export async function syncSaveCustomTimeframes(timeframes: CustomTimeframe[]): Promise<void> {
  localSaveCustomTimeframes(timeframes);

  if (!canSync()) return;

  try {
    await syncApi.put(`${API_PREFIX}/settings/timeframes`, { timeframes });
  } catch (e) {
    console.warn("[Sync] Failed to push custom timeframes to server:", e);
  }
}

/** Load custom timeframes: try server first, fall back to localStorage. */
export async function syncLoadCustomTimeframes(): Promise<CustomTimeframe[]> {
  if (!canSync()) return localLoadCustomTimeframes();

  try {
    const response = await syncApi.get(`${API_PREFIX}/settings/timeframes`);
    const serverTimeframes = response.data?.timeframes;
    if (Array.isArray(serverTimeframes)) {
      localSaveCustomTimeframes(serverTimeframes);
      return serverTimeframes;
    }
  } catch (e) {
    console.warn("[Sync] Failed to load custom timeframes from server, using localStorage:", e);
  }

  return localLoadCustomTimeframes();
}

// ── Full Sync on Login ────────────────────────────────────────────

/**
 * Pull all user data from the server after login.
 * Called once after successful authentication to hydrate local state.
 */
export async function pullAllOnLogin(): Promise<void> {
  if (!canSync()) return;

  try {
    await Promise.allSettled([
      syncLoadChartLayout(),
      syncLoadChartSettings(),
      syncLoadTemplates(),
      syncLoadCustomTimeframes(),
      syncGetWatchlists(),
      syncGetUserSettings(),
    ]);
  } catch (e) {
    console.warn("[Sync] Partial failure during login sync:", e);
  }
}
